/**
 * A flat list of every path, turned into something a person can look at.
 *
 * Found on glass, 28 July. The backend returns every file under the workspace,
 * which is right for an API and wrong for a screen: one `pip install` put 215
 * files in a workspace, each listed on its own row with a hundred-character
 * path, and the phone showed a wall of `.pyc`. The panel was fine until the
 * moment the feature it exists for actually worked.
 *
 * So: one level at a time. A folder is one row that says how many things are
 * in it, and opening it shows that level. This is a view, not a filter, so
 * nothing is hidden from anyone who goes looking.
 */

/** Entries directly inside `prefix`, folders collapsed into one row each. */
export function levelAt(entries, prefix = "") {
  const inside = prefix ? `${prefix}/` : "";
  const files = [];
  const folders = new Map();

  for (const entry of entries) {
    if (prefix && !entry.path.startsWith(inside)) continue;
    const rest = entry.path.slice(inside.length);
    if (!rest) continue;

    const slash = rest.indexOf("/");
    if (slash === -1) {
      if (!entry.is_dir) files.push({ ...entry, name: rest, kind: "file" });
      continue;
    }
    const name = rest.slice(0, slash);
    const folder = folders.get(name) || { name, kind: "folder", path: `${inside}${name}`, count: 0, size: 0 };
    if (!entry.is_dir) {
      folder.count += 1;
      folder.size += entry.size || 0;
    }
    folders.set(name, folder);
  }

  // Folders first, then files, each by name. A person scanning for their own
  // file should not have to read past a hundred generated ones.
  return [
    ...[...folders.values()].sort((a, b) => a.name.localeCompare(b.name)),
    ...files.sort((a, b) => a.name.localeCompare(b.name)),
  ];
}

/** The steps back out of a folder, for the trail at the top. */
export function crumbs(prefix = "") {
  if (!prefix) return [];
  const parts = prefix.split("/");
  return parts.map((name, index) => ({ name, path: parts.slice(0, index + 1).join("/") }));
}

/** Human size, because 4823 bytes means nothing at a glance. */
export function readableSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}
